import { useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import type {
  Control,
  UseFormClearErrors,
  UseFormTrigger,
  UseFormWatch,
  UseFormReset
} from 'react-hook-form'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import type { CAR_FORM_STATE_TYPE } from '../types'

type UseCarFormProps = {
  control: Control<CAR_FORM_STATE_TYPE>
  watch: UseFormWatch<CAR_FORM_STATE_TYPE>
  clearErrors: UseFormClearErrors<CAR_FORM_STATE_TYPE>
  trigger: UseFormTrigger<CAR_FORM_STATE_TYPE>
  initialValues: CAR_FORM_STATE_TYPE
  setLocation?: (location: LOCATION_SEARCH_TYPE) => void
  updateFormValues?: (values: CAR_FORM_STATE_TYPE) => void
  reset: UseFormReset<CAR_FORM_STATE_TYPE>
}

// Hook that attaches react-hook-form watch elements to each of the needed submitted values.
//  This tracks when specific values change to give more granular control over rerenders.
const useCarFormSubmitValues = ({
  control,
  watch,
  clearErrors,
  trigger,
  initialValues,
  setLocation,
  updateFormValues,
  reset
}: UseCarFormProps) => {
  // Watch all form fields and prepop or populate with initialValues
  const watchTripType = useWatch({
    control,
    name: 'tripType',
    defaultValue: initialValues.tripType
  })

  const watchStartLocation = useWatch({
    control,
    name: 'startLocation'
  })

  const watchEndLocation = useWatch({
    control,
    name: 'endLocation'
  })

  const watchStartDate = useWatch({
    control,
    name: 'startDate'
  })

  const watchEndDate = useWatch({
    control,
    name: 'endDate'
  })

  const watchStartTime = useWatch({
    control,
    name: 'startTime',
    defaultValue: initialValues?.startTime
  })

  const watchEndTime = useWatch({
    control,
    name: 'endTime',
    defaultValue: initialValues?.endTime
  })

  const watchHotelStartDate = useWatch({
    control,
    name: 'hotelStartDate'
  })

  const watchHotelEndDate = useWatch({
    control,
    name: 'hotelEndDate'
  })

  const watchTravelers = useWatch({
    control,
    name: 'travelers',
    defaultValue: initialValues?.travelers
  })

  // Form effects - watch subscription for updateFormValues
  useEffect(() => {
    const subscription = watch(value => {
      if (updateFormValues) {
        updateFormValues(value as CAR_FORM_STATE_TYPE)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setLocation, updateFormValues])

  useEffect(() => {
    clearErrors('startLocation')
  }, [clearErrors, watchStartLocation])

  useEffect(() => {
    clearErrors('endLocation')
  }, [clearErrors, watchEndLocation])

  useEffect(() => {
    clearErrors('startDate')
  }, [clearErrors, watchStartDate, watchTripType])

  useEffect(() => {
    clearErrors('endDate')
  }, [clearErrors, watchEndDate, watchTripType])

  // Clear errors when hotel dates change
  useEffect(() => {
    clearErrors('hotelStartDate')
  }, [clearErrors, watchHotelStartDate, watchTripType])

  useEffect(() => {
    clearErrors('hotelEndDate')
  }, [clearErrors, watchHotelEndDate, watchTripType])

  // Trigger validation when travelers or trip type changes
  useEffect(() => {
    if (watchTravelers) {
      void trigger('travelers.childrenAges')
      void trigger('travelers.children')
      void trigger('travelers.rooms')
    }
  }, [trigger, watchTripType, watchTravelers])

  // Function to reset form to initial values and clear all errors
  const resetFormToInitialValues = (options?: {
    preserveTripType?: string
    preserveDifferentDropOff?: boolean
  }) => {
    clearErrors()
    const resetValues = {
      ...initialValues,
      ...(options?.preserveTripType && { tripType: options.preserveTripType }),
      ...(options?.preserveDifferentDropOff !== undefined && {
        oneWay: options.preserveDifferentDropOff
      })
    }
    reset(resetValues)
  }

  return {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchStartTime,
    watchEndTime,
    watchHotelStartDate,
    watchHotelEndDate,
    watchTravelers,
    resetFormToInitialValues
  }
}

export default useCarFormSubmitValues
